import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CreditCard as CreditCardIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import {
  SubscriptionPaymentMethod,
  formatSubscriptionPaymentMethodExpiry,
  formatSubscriptionPaymentMethodLabel,
} from '../../models/Subscription';

export interface SubscriptionPaymentMethodPanelProps {
  /** Opens Stripe Billing Portal to change the default method. */
  onManage?: () => void;
  manageLoading?: boolean;
}

/**
 * Default Stripe payment method for the host organization subscription.
 * Aesthetic: calm billing strip — one job (show card / empty), portal for changes.
 */
const SubscriptionPaymentMethodPanel: React.FC<SubscriptionPaymentMethodPanelProps> = ({
  onManage,
  manageLoading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [method, setMethod] = useState<SubscriptionPaymentMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hostApiClient.getSubscriptionPaymentMethod();
      setMethod(response.paymentMethod);
    } catch (err) {
      console.error('Error loading subscription payment method:', err);
      setError(t('subscription.paymentMethod.fetchError', 'Failed to load payment method'));
      setMethod(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const expiry = method ? formatSubscriptionPaymentMethodExpiry(method) : null;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <CreditCardIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            component="h2"
            fontWeight={800}
            sx={{ letterSpacing: '-0.01em' }}
          >
            {t('subscription.paymentMethod.title', 'Payment method')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'subscription.paymentMethod.subtitle',
              'Default card used for subscription invoices. Change it securely in Stripe.',
            )}
          </Typography>
        </Box>
      </Stack>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={100} px={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void load()}>
                {t('subscription.paymentMethod.retry', 'Retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {!loading && !error && !method && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t(
              'subscription.paymentMethod.empty',
              'No default payment method on file yet. Add one in Stripe when you are ready to bill.',
            )}
          </Typography>
          {onManage && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                manageLoading ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />
              }
              disabled={manageLoading}
              onClick={onManage}
              sx={{ fontWeight: 700, flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {t('subscription.paymentMethod.manage', 'Manage in Stripe')}
            </Button>
          )}
        </Stack>
      )}

      {!loading && !error && method && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
              {formatSubscriptionPaymentMethodLabel(method)}
            </Typography>
            {expiry && (
              <Typography variant="body2" color="text.secondary">
                {t('subscription.paymentMethod.expires', 'Expires {{expiry}}', { expiry })}
              </Typography>
            )}
          </Stack>
          {onManage && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                manageLoading ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />
              }
              disabled={manageLoading}
              onClick={onManage}
              sx={{ fontWeight: 700, flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {t('subscription.paymentMethod.manage', 'Manage in Stripe')}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default SubscriptionPaymentMethodPanel;
